import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { User, Building2, Wallet, Bell, Bookmark, History, Loader, Save, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { analyzeLocation } from '../services/locationService';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || ''
);

interface UserProfile {
  id: string;
  full_name: string;
  company_name: string;
  business_type: string;
  preferred_budget_range: string;
  notification_preferences: {
    email: boolean;
    push: boolean;
  };
}

interface SavedLocation {
  id: string;
  location_id: string;
  notes: string;
  created_at: string;
  location_analysis: {
    location: string;
    business_type: string;
    budget: string;
  };
}

const defaultProfile = {
  full_name: '',
  company_name: '',
  business_type: '',
  preferred_budget_range: '',
  notification_preferences: {
    email: true,
    push: false,
  },
};

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
  const [searchHistory, setSearchHistory] = useState<any[]>([]);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login', { state: { returnTo: '/profile' } });
        return;
      }

      // Load profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      // If no profile exists, create a default one
      if (profileError && profileError.code === 'PGRST116') {
        const { data: newProfile, error: createError } = await supabase
          .from('user_profiles')
          .insert([{ ...defaultProfile, user_id: session.user.id }])
          .select()
          .single();

        if (createError) throw createError;
        setProfile(newProfile);
      } else if (profileError) {
        throw profileError;
      } else {
        setProfile(profileData);
      }

      // Load saved locations
      const { data: savedLocationsData } = await supabase
        .from('saved_locations')
        .select(`
          *,
          location_analysis (
            location,
            business_type,
            budget
          )
        `)
        .order('created_at', { ascending: false });

      if (savedLocationsData) {
        setSavedLocations(savedLocationsData);
      }

      // Load search history
      const { data: historyData } = await supabase
        .from('search_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (historyData) {
        setSearchHistory(historyData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          ...profile,
          user_id: session.user.id,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    try {
      const { error } = await supabase
        .from('saved_locations')
        .delete()
        .eq('id', locationId);

      if (error) throw error;
      setSavedLocations(prev => prev.filter(loc => loc.id !== locationId));
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  const handleHistoryClick = async (search: any) => {
    try {
      setLoading(true);
      const analysis = await analyzeLocation(
        search.location,
        search.business_type,
        search.budget
      );
      
      navigate('/results', {
        state: {
          searchData: {
            location: search.location,
            businessType: search.business_type,
            budget: search.budget
          },
          analysis
        }
      });
    } catch (error) {
      console.error('Error analyzing location:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <User className="mr-2 text-primary" />
          Profile Settings
        </h2>

        <form onSubmit={handleProfileUpdate} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Full Name</label>
              <input
                type="text"
                value={profile?.full_name || ''}
                onChange={e => setProfile(prev => ({ ...prev!, full_name: e.target.value }))}
                className="w-full p-2 bg-card border border-border rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Company Name</label>
              <input
                type="text"
                value={profile?.company_name || ''}
                onChange={e => setProfile(prev => ({ ...prev!, company_name: e.target.value }))}
                className="w-full p-2 bg-card border border-border rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Business Type</label>
              <input
                type="text"
                value={profile?.business_type || ''}
                onChange={e => setProfile(prev => ({ ...prev!, business_type: e.target.value }))}
                className="w-full p-2 bg-card border border-border rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Preferred Budget Range</label>
              <input
                type="text"
                value={profile?.preferred_budget_range || ''}
                onChange={e => setProfile(prev => ({ ...prev!, preferred_budget_range: e.target.value }))}
                className="w-full p-2 bg-card border border-border rounded-lg"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Notification Preferences</label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={profile?.notification_preferences?.email || false}
                  onChange={e => setProfile(prev => ({
                    ...prev!,
                    notification_preferences: {
                      ...prev!.notification_preferences,
                      email: e.target.checked
                    }
                  }))}
                  className="rounded border-border"
                />
                <span>Email Notifications</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={profile?.notification_preferences?.push || false}
                  onChange={e => setProfile(prev => ({
                    ...prev!,
                    notification_preferences: {
                      ...prev!.notification_preferences,
                      push: e.target.checked
                    }
                  }))}
                  className="rounded border-border"
                />
                <span>Push Notifications</span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-primary text-primary-foreground py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader className="animate-spin mr-2" size={20} />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2" size={20} />
                Save Changes
              </>
            )}
          </button>
        </form>
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <Bookmark className="mr-2 text-primary" />
          Saved Locations
        </h2>

        <div className="space-y-4">
          {savedLocations.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No saved locations yet
            </p>
          ) : (
            savedLocations.map(location => (
              <div
                key={location.id}
                className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{location.location_analysis.location}</h3>
                    <p className="text-sm text-muted-foreground">
                      {location.location_analysis.business_type} • {location.location_analysis.budget}
                    </p>
                    <p className="text-sm mt-2">{location.notes || 'No notes added'}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteLocation(location.id)}
                    className="text-destructive hover:text-destructive/90 transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <History className="mr-2 text-primary" />
          Recent Searches
        </h2>

        <div className="space-y-4">
          {searchHistory.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No search history yet
            </p>
          ) : (
            searchHistory.map(search => (
              <button
                key={search.id}
                onClick={() => handleHistoryClick(search)}
                className="w-full text-left border border-border rounded-lg p-4 hover:bg-card/50 hover:border-primary/50 transition-colors"
              >
                <h3 className="font-semibold">{search.location}</h3>
                <p className="text-sm text-muted-foreground">
                  {search.business_type} • {search.budget}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(search.created_at).toLocaleDateString()}
                </p>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;